import { useState, useEffect } from "react";
import { Box, IconButton, InputAdornment, Stack } from "@mui/material";
import { Custom } from "components";
import { useApp, useTheme } from "contexts";

// icons
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutlineRounded,
  RadioButtonUncheckedRounded,
} from "@mui/icons-material";

type Props = {
  onPasswordChange: (password: string) => void;
  onPasswordValid: (isValid: boolean) => void;
};

export const Password = ({ onPasswordChange, onPasswordValid }: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  // Password validation rules
  const validations = [
    { text: t.validation.password.min_length, valid: password.length >= 8 },
    { text: t.validation.password.one_number, valid: /\d/.test(password) },
    {
      text: t.validation.password.lower_upper_case,
      valid: /[a-z]/.test(password) && /[A-Z]/.test(password),
    },
    {
      text: t.validation.password.special_character,
      valid: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
    {
      text: t.validation.password.match,
      valid: confirmPassword.length > 0 && password === confirmPassword,
    },
  ];

  const isPasswordValid = validations.every((rule) => rule.valid);
  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;

  // Trigger callbacks
  // useEffect(() => {
  //   onPasswordChange(password);
  //   onPasswordValid(isPasswordValid);
  // }, [password, isPasswordValid, onPasswordChange, onPasswordValid]);

  useEffect(() => {
    onPasswordChange(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  useEffect(() => {
    onPasswordValid(isPasswordValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPasswordValid]);

  return (
    <Box>
      <Stack spacing={theme.spacing.sm}>
        {/* Create Password */}
        <Custom.TextField
          placeholder={t.placeholder.create_password}
          type={showPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  tabIndex={-1}
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Confirm Password */}
        <Custom.TextField
          placeholder={t.placeholder.confirm_password}
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword.length > 0 && !passwordsMatch}
          // helperText={
          //   confirmPassword.length > 0 && !passwordsMatch ? (
          //     <Stack
          //       direction="row"
          //       alignItems="center"
          //       spacing={theme.spacing.sm}
          //     >
          //       <ErrorOutline
          //         sx={{
          //           fontSize: theme.font.sm,
          //           color: theme.color.status.error,
          //         }}
          //       />
          //       <Custom.Typography
          //         size={theme.font.xs}
          //         sx={{ color: theme.color.status.error }}
          //       >
          //         {t.validation.password_mismatch}
          //       </Custom.Typography>
          //     </Stack>
          //   ) : (
          //     ""
          //   )
          // }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  tabIndex={-1}
                  onClick={toggleConfirmPasswordVisibility}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Password Strength Feedback */}
        <Box px={theme.spacing.sm}>
          <Stack spacing={theme.spacing.xs}>
            {validations.map(({ text, valid }, index) => (
              <Box key={index}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={theme.spacing.sm}
                >
                  {valid ? (
                    <CheckCircleOutlineRounded
                      sx={{
                        fontSize: theme.font.sm,
                        color: theme.color.status.success,
                      }}
                    />
                  ) : (
                    <RadioButtonUncheckedRounded
                      sx={{
                        fontSize: theme.font.sm,
                        color: theme.color.font.color,
                      }}
                    />
                  )}
                  <Custom.Typography
                    size={theme.font.xs}
                    sx={{
                      color: valid
                        ? theme.color.status.success
                        : theme.color.font.color,
                    }}
                  >
                    {text}
                  </Custom.Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
